import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import MonthNavigator from '../../../components/MonthNavigator';
import { useToast } from '../../../components/Toast';
import { nowKST } from '../../../utils/date';
import { numFmt, PAYOUT_LABELS } from './IncentiveRuleModal.constants';
import { stdBlackBtn, stdOutlineBtn, stdBadgeActive } from '../adminlayout/designStandard';

const card = {
  background: 'var(--bg-card, #fff)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: 16,
  marginBottom: 10,
};
const badge = (color) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 700,
  background: `${color}22`,
  color,
});
const input = {
  padding: '5px 8px',
  borderRadius: 6,
  border: '1px solid var(--border)',
  color: 'var(--text)',
  background: 'var(--bg-card)',
  boxSizing: 'border-box',
};

function componentLabel(component) {
  return component.ticket_type_name || component.label || component.source || '-';
}

export default function IncentiveUserDetail({ companyCode }) {
  const { showToast } = useToast();
  const now = nowKST();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [defaultMonthReady, setDefaultMonthReady] = useState(false);
  const userPickedMonthRef = useRef(false);
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editAmt, setEditAmt] = useState('');
  const [editMemo, setEditMemo] = useState('');

  const load = useCallback(async () => {
    if (!companyCode || !defaultMonthReady) return;
    const res = await apiClient.get(`/api/admin/incentive/records/by-user?company_code=${companyCode}&year=${year}&month=${month}`);
    if (res.ok) setUsers(res.data?.data || []);
  }, [companyCode, defaultMonthReady, year, month]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    userPickedMonthRef.current = false;
    setDefaultMonthReady(false);

    async function applyDefaultMonth() {
      const res = await apiClient.get(`/api/admin/incentive/default-month?company_code=${companyCode}`);
      if (alive && res.ok && res.data?.data && !userPickedMonthRef.current) {
        setYear(Number(res.data.data.year));
        setMonth(Number(res.data.data.month));
      }
      if (alive) setDefaultMonthReady(true);
    }

    if (companyCode) applyDefaultMonth();
    else setDefaultMonthReady(true);
    return () => { alive = false; };
  }, [companyCode]);

  function handleMonthChange(next) {
    userPickedMonthRef.current = true;
    setYear(Number(next.year));
    setMonth(Number(next.month));
  }

  async function handleDefaultMonth() {
    if (!companyCode) return;
    const res = await apiClient.get(`/api/admin/incentive/default-month?company_code=${companyCode}`);
    if (!res.ok || !res.data?.data) return;
    userPickedMonthRef.current = false;
    setYear(Number(res.data.data.year));
    setMonth(Number(res.data.data.month));
  }

  async function handleSaveOverride(id) {
    const amount = Number(String(editAmt).replace(/,/g, ''));
    if (Number.isNaN(amount)) {
      showToast('올바른 금액을 입력해 주세요.', 'error');
      return;
    }
    const res = await apiClient.put(`/api/admin/incentive/records/${id}`, {
      calculated_amount: amount,
      memo: editMemo || null,
      is_manual_override: true,
    });
    if (res.ok) {
      showToast('수동 조정 저장 완료', 'success');
      setEditId(null);
      load();
    } else {
      showToast(res.data?.message || '저장 실패', 'error');
    }
  }

  async function handleRestore(id) {
    if (!window.confirm('수동 조정을 해제하고 자동 계산 금액으로 복원할까요?')) return;
    const res = await apiClient.put(`/api/admin/incentive/records/${id}`, { is_manual_override: false });
    if (res.ok) {
      showToast('자동 계산 복원 완료', 'success');
      load();
    } else {
      showToast(res.data?.message || '복원 실패', 'error');
    }
  }

  function renderBreakdown(record) {
    const breakdown = record.score_breakdown || {};
    const components = breakdown.components || [];
    const payout = breakdown.payout || {};
    const payoutMethod = record.payout_method || {};
    const condition = breakdown.conditions;

    return (
      <div style={{ background: 'var(--bg, #f8f9fa)', borderRadius: 8, padding: 12, marginTop: 8, fontSize: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--text)' }}>{record.rule_name}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <span style={stdBadgeActive}>{PAYOUT_LABELS[payoutMethod.type] || payoutMethod.type}</span>
            {record.is_manual_override && <span style={badge('#ef4444')}>수동 조정</span>}
          </div>
        </div>

        {components.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: 4, color: 'var(--text-muted)' }}>항목</th>
                <th style={{ textAlign: 'right', padding: 4, color: 'var(--text-muted)' }}>수량</th>
                <th style={{ textAlign: 'center', padding: 4, color: 'var(--text-muted)' }}>가중치</th>
                <th style={{ textAlign: 'right', padding: 4, color: 'var(--text-muted)' }}>점수</th>
              </tr>
            </thead>
            <tbody>
              {components.map((component, index) => (
                <tr key={`${component.ticket_type_id || component.source || 'row'}-${index}`} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: 4, color: 'var(--text)' }}>{componentLabel(component)}</td>
                  <td style={{ textAlign: 'right', padding: 4, color: 'var(--text)' }}>
                    {numFmt(component.qty || component.net_qty || 0)}
                    {Number(component.cancelled_qty || 0) > 0 && (
                      <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 700 }}>
                        취소차감 -{numFmt(component.cancelled_qty)}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', padding: 4, color: 'var(--text-muted)' }}>x{component.weight}</td>
                  <td style={{ textAlign: 'right', padding: 4, fontWeight: 700, color: 'var(--text)' }}>{numFmt(component.score)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--border)' }}>
                <td colSpan={3} style={{ padding: 4, fontWeight: 800, color: 'var(--text)' }}>총 점수</td>
                <td style={{ textAlign: 'right', padding: 4, fontWeight: 900, fontSize: 13, color: 'var(--std-accent-text, #1d7de6)' }}>{numFmt(breakdown.rawScore)}</td>
              </tr>
            </tbody>
          </table>
        )}

        {condition?.type === 'contract_rate_min' && (
          <div style={{ padding: '4px 0', color: condition.met ? '#059669' : '#ef4444' }}>
            계약률 {condition.actual}% / 기준 {condition.required}% {condition.met ? '충족' : '미달'}
          </div>
        )}

        <div style={{ padding: '6px 0', borderTop: '1px solid var(--border)', marginTop: 4, color: 'var(--text)' }}>
          {payoutMethod.type === 'threshold_excess' && (
            <div>
              기준 {numFmt(payoutMethod.threshold)}점
              {Number(breakdown.rawScore) >= Number(payoutMethod.threshold)
                ? <> / 초과 {numFmt(payout.excess || Math.max(0, breakdown.rawScore - payoutMethod.threshold))}점 x {numFmt(payoutMethod.unit_price)}원</>
                : <span style={{ color: '#ef4444' }}> / 기준 미달</span>}
            </div>
          )}
          {payoutMethod.type === 'per_point' && <div>{numFmt(breakdown.rawScore)}점 x {numFmt(payoutMethod.unit_price)}원</div>}
          {payoutMethod.type === 'fixed' && <div>고정 금액: {numFmt(payoutMethod.amount)}원</div>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {editId === record.id ? (
              <>
                <input type="text" value={editAmt} onChange={(e) => setEditAmt(e.target.value)} placeholder="금액" style={{ ...input, width: 100, textAlign: 'right' }} />
                <input type="text" value={editMemo} onChange={(e) => setEditMemo(e.target.value)} placeholder="메모" style={{ ...input, width: 160 }} />
                <button style={stdBlackBtn} onClick={() => handleSaveOverride(record.id)}>저장</button>
                <button style={stdOutlineBtn} onClick={() => setEditId(null)}>취소</button>
              </>
            ) : (
              <>
                <button
                  style={{ ...stdOutlineBtn, fontSize: 11, padding: '4px 10px' }}
                  onClick={() => { setEditId(record.id); setEditAmt(String(record.calculated_amount || 0)); setEditMemo(record.memo || ''); }}
                >
                  금액 수정
                </button>
                {record.is_manual_override && (
                  <button style={{ ...stdOutlineBtn, fontSize: 11, padding: '4px 10px', color: '#ef4444' }} onClick={() => handleRestore(record.id)}>
                    자동 계산 복원
                  </button>
                )}
              </>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            {record.is_manual_override && record.score_breakdown?.payout?.amount != null && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'line-through' }}>
                {numFmt(record.score_breakdown.payout.amount)}원
              </div>
            )}
            <div style={{ fontSize: 16, fontWeight: 900, color: record.is_manual_override ? '#ef4444' : (Number(record.calculated_amount) > 0 ? '#059669' : 'var(--text-muted)') }}>
              {numFmt(record.calculated_amount)}원
              {record.memo && <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>({record.memo})</span>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <MonthNavigator year={year} month={month} onChange={handleMonthChange} onReset={handleDefaultMonth} showReset disabled={!defaultMonthReady} compact />
      </div>

      {users.length === 0 && (
        <div style={{ color: 'var(--text-muted)', padding: 20, textAlign: 'center' }}>
          {year}년 {month}월 인센티브 실적이 없습니다.
        </div>
      )}

      {users.map((user) => {
        const isExpanded = expanded === user.user_id;
        return (
          <div key={user.user_id} style={{ ...card, borderLeft: `4px solid ${user.total > 0 ? 'var(--std-accent, #1d7de6)' : '#999'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div>
                <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)', marginRight: 8 }}>{user.user_name}</span>
                <span style={stdBadgeActive}>{user.rules.length}개 규칙</span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, color: user.total > 0 ? '#059669' : 'var(--text-muted)' }}>
                {numFmt(user.total)}원
              </div>
            </div>

            <button
              onClick={() => setExpanded(isExpanded ? null : user.user_id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--std-accent-text, #1d7de6)', fontWeight: 700, fontSize: 12, marginTop: 6 }}
            >
              {isExpanded ? '접기' : `상세 보기 (${user.rules.length}개 규칙)`}
            </button>

            {isExpanded && user.rules.map((record) => <div key={record.id}>{renderBreakdown(record)}</div>)}
          </div>
        );
      })}
    </div>
  );
}
